import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import {
  metodologiasPadrao, minhasFichasParceiro, acessosParceiro, criarFichaParceiro, obterFichaParceiro, salvarFichaParceiro,
  enviarFichaParceiro, enviarLogoParceiro, conferirDocumentoParceiro, urlApi,
} from "../services/api";

// 🎓 CADASTRO DE PARCEIRO DE CAPACITAÇÃO (22/10/2026) — mockup aprovado pelo Nelson: mokups/cadastro-parceiro-escola.html.
// 8 etapas; cada "Salvar e continuar" grava a ficha no servidor (dá para parar e continuar depois). Duas vertentes:
// escola/franquia (BOLSA %, aulas na plataforma da escola) × professor autônomo (AULA EXPERIMENTAL GRÁTIS).
// Enviar → "Aguardando aprovação" → o dono aprova no admin → /capacitacao/{slug} no ar.
// ⚠️ Texto nunca associa curso a vaga/emprego (o servidor recusa no envio). Contatos NÃO aparecem para o candidato.

const PASSOS = ["Tipo", "Identificação", "Responsáveis", "Sobre", "Cursos", "Condições", "Compromissos", "Revisar e enviar"];
const COMPROMISSOS = [
  "Faço o primeiro contato em até 2 dias úteis.",
  "No máximo 3 tentativas de contato, sem insistência.",
  "Nunca associo o curso a vaga ou emprego, nem uso o nome do eaiRH em propaganda.",
  "Uso os dados do candidato só para este curso e apago se ele retirar a autorização.",
  "Respondo às avaliações dos alunos com respeito.",
  "Informo ao eaiRH quando o candidato for atendido (para o índice de resposta da minha página).",
];
const IDIOMAS = ["Inglês", "Espanhol", "Francês", "Alemão", "Italiano", "Libras", "Mandarim", "Japonês"];
const NIVEIS = ["Do zero", "Básico", "Intermediário", "Avançado"];
const AREAS = [["idiomas", "🌎 Idiomas"], ["excel", "📊 Excel / dados"], ["tecnologia", "💻 Tecnologia"], ["outra", "Outra"]];
const ONDE = [["sala", "Online (sala do eaiRH)"], ["propria", "Online (minha plataforma)"], ["presencial", "Presencial"]];
const UNIDADES = [["horas", "horas"], ["dias", "dias"], ["semanas", "semanas"], ["meses", "meses"], ["anos", "anos"], ["sem-prazo", "sem prazo definido"]];

const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-100 disabled:text-gray-400";
const contatoVazio = () => ({ nome: "", cargo: "", email: "", telefone: "", recebe: true, principal: false, canal: "email" });
const iniciais = (nome) => (nome || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
const soDigitos = (s) => (s || "").replace(/\D/g, "");
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function Rotulo({ children, dica }) {
  return <div className="text-[13px] font-semibold text-gray-800 mt-3 mb-1">{children}{dica && <span className="font-normal text-gray-500"> {dica}</span>}</div>;
}
function Chip({ ativo, onClick, children, disabled }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick}
      className={`rounded-full border px-3 py-1 text-[13px] ${ativo ? "bg-primary text-white border-primary" : "bg-white text-gray-700 border-gray-300 hover:border-primary"} disabled:opacity-50`}>
      {children}
    </button>
  );
}
const Aviso = ({ children }) => <div className="mt-3 rounded border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-[13px] text-gray-800">{children}</div>;

// nome que aparece no título da página (mesma regra do servidor)
function nomeExibicao(d) {
  if (d.tipo === "prof") return d.nome || "";
  const marca = d.marca || d.nome || "";
  return d.franquia === true && d.unidade ? `${marca} · ${d.unidade}` : marca;
}
function cursoDe(d) {
  if (d.area === "idiomas") { const l = d.idiomas || []; return l.length <= 1 ? l.join("") || "Idiomas" : `${l.slice(0, -1).join(", ")} e ${l[l.length - 1]}`; }
  return { excel: "Excel e dados", tecnologia: "Tecnologia" }[d.area] || d.areaOutra || "Curso";
}
function duracaoDe(d) {
  if (d.duracaoUnidade === "sem-prazo") return "Sem prazo definido";
  if (!d.duracaoNum || !d.duracaoUnidade) return null;
  const um = { horas: "hora", dias: "dia", semanas: "semana", meses: "mês", anos: "ano" }[d.duracaoUnidade];
  return `${d.duracaoNum} ${d.duracaoNum === 1 ? um : d.duracaoUnidade}`;
}
function frequenciaDe(d) {
  const p = [];
  const por = { dia: "dia", mes: "mês" }[d.freqMinPor] || "semana";
  if (d.freqMin) p.push(`Mínimo de ${d.freqMin} aula${d.freqMin === 1 ? "" : "s"} por ${por}, ${d.freqSemLimite || !d.freqMax ? "sem máximo" : `máximo de ${d.freqMax}`}`);
  if (d.aulaDuracaoNum) p.push(`aulas de ${d.aulaDuracaoNum} ${d.aulaDuracaoUnidade === "h" ? (d.aulaDuracaoNum === 1 ? "hora" : "horas") : "min"}`);
  if (d.freqObs) p.push(d.freqObs);
  return p.join(" · ") || null;
}

export default function ParceiroCadastroPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const q = new URLSearchParams(location.search);
  const idUrl = q.get("id");
  const [ficha, setFicha] = useState(null);          // metadados (id, slug, status…) — null = ainda não criada
  const [dados, setDados] = useState(null);
  const [passo, setPasso] = useState(0);
  const [padroes, setPadroes] = useState([]);
  const [erro, setErro] = useState("");
  const [pendencias, setPendencias] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [salvoEm, setSalvoEm] = useState(null);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    document.title = "Cadastro de parceiro de capacitação · eaiRH";
    if (!user) {
      navigate(`/entrar?cadastro=1&tipo=parceiro&redirect=${encodeURIComponent(location.pathname + location.search)}&motivo=${encodeURIComponent("Crie sua conta de parceiro (professor ou escola) para começar o cadastro.")}`);
      return;
    }
    if (user.tipo !== 3) return;
    metodologiasPadrao().then((l) => setPadroes(l || [])).catch(() => {});
    const carregar = async () => {
      try {
        if (idUrl) {
          const f = await obterFichaParceiro(idUrl);
          setFicha(f); setDados(f.dados); setPasso(Math.min(f.passo || 0, 7));
          return;
        }
        if (q.get("nova") !== "1") {
          const l = await minhasFichasParceiro();
          if (l && l.length > 0) { navigate("/parceiro", { replace: true }); return; }
          // 22/10/2026: é contato de uma ficha da escola (mesmo domínio)? O painel oferece "pedir acesso" em vez de outro cadastro.
          const ac = await acessosParceiro().catch(() => []);
          if ((ac || []).length > 0) { navigate("/parceiro", { replace: true }); return; }
        }
        setDados({ tipo: q.get("tipo") === "prof" ? "prof" : "escola" }); setPasso(0);
      } catch (e) { setErro(e.message); }
    };
    carregar();
  }, [user, idUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;
  if (user.tipo !== 3) {
    return (
      <div className="min-h-screen bg-background"><Header />
        <main className="max-w-xl mx-auto px-4 py-10 text-sm text-gray-700">
          <h1 className="text-xl font-bold text-text">Cadastro de parceiro de capacitação</h1>
          <p className="mt-2">Esta área é para contas de parceiro (escola ou professor). Saia da sua conta e crie uma conta do tipo <b>Professor / escola</b>.</p>
        </main>
      </div>
    );
  }

  const muda = (k, v) => { setDados((d) => ({ ...d, [k]: v })); setSalvoEm(null); };
  const aprovado = ficha?.status === "aprovado";

  const salvar = async (proximo) => {
    setErro(""); setSalvando(true);
    try {
      let f = ficha;
      if (!f) {
        f = await criarFichaParceiro(dados.tipo);
        // a ficha nova traz o 1º contato com os dados da conta: mescla com o que já foi escolhido aqui
        const r = await salvarFichaParceiro(f.id, proximo, { ...f.dados, ...dados, contatos: dados.contatos || f.dados.contatos });
        setFicha(r); setDados(r.dados);
        navigate(`/parceiro/cadastro?id=${f.id}`, { replace: true });   // recarregar a página continua desta ficha
      } else {
        const r = await salvarFichaParceiro(f.id, proximo, dados);
        setFicha(r); setDados(r.dados);
      }
      setSalvoEm(new Date()); setPasso(proximo); window.scrollTo(0, 0);
    } catch (e) { setErro(e.message); }
    setSalvando(false);
  };

  const enviar = async () => {
    setErro(""); setPendencias([]); setSalvando(true);
    try {
      await salvarFichaParceiro(ficha.id, 7, dados);
      const r = await enviarFichaParceiro(ficha.id);
      setFicha(r); setEnviado(true); window.scrollTo(0, 0);
    } catch (e) {
      setErro(e.message);
      if (e.pendencias) setPendencias(e.pendencias);
    }
    setSalvando(false);
  };

  if (!dados) {
    return <div className="min-h-screen bg-background"><Header /><p className="p-8 text-gray-400">{erro || "Carregando…"}</p></div>;
  }

  if (enviado) {
    return (
      <div className="min-h-screen bg-background"><Header />
        <main className="max-w-2xl mx-auto px-4 py-10">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h1 className="text-xl font-bold text-text">✅ {aprovado ? "Alterações enviadas para revisão" : "Cadastro enviado!"}</h1>
            <p className="mt-2 text-sm text-gray-700">
              {aprovado
                ? "Sua página continua no ar com a versão aprovada. Assim que o eaiRH conferir, as alterações entram na página e você recebe um e-mail."
                : "Recebemos seus dados. O eaiRH confere e aprova, normalmente no mesmo dia. Você recebe um e-mail quando sua página estiver no ar."}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/parceiro" className="bg-accent text-white font-bold rounded-lg px-4 py-2 text-sm">Ir para o meu painel</Link>
              <Link to={`/capacitacao/${ficha.slug}?previa=1`} className="border border-gray-300 rounded-lg px-4 py-2 text-sm font-semibold text-gray-700">Ver a prévia da página</Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const telas = [
    <PassoTipo key="t" dados={dados} muda={muda} travado={aprovado} />,
    <PassoIdentificacao key="i" dados={dados} muda={muda} ficha={ficha} setFicha={setFicha} />,
    <PassoContatos key="c" dados={dados} muda={muda} />,
    <PassoSobre key="s" dados={dados} muda={muda} padroes={padroes} />,
    <PassoCursos key="cu" dados={dados} muda={muda} />,
    <PassoCondicoes key="co" dados={dados} muda={muda} />,
    <PassoCompromissos key="cp" dados={dados} muda={muda} />,
    <PassoRevisar key="r" dados={dados} ficha={ficha} pendencias={pendencias} irPara={(p) => { setPasso(p); setPendencias([]); setErro(""); }} />,
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <h1 className="text-2xl font-bold text-primary">Cadastro de parceiro de capacitação</h1>
        <p className="text-sm text-gray-500 mt-1">Escolas e professores que oferecem uma condição especial para os candidatos do eaiRH. Gratuito. Cada etapa salva sozinha; dá para parar e continuar depois.</p>

        {ficha?.motivoRecusa && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            <b>O eaiRH pediu um ajuste:</b> {ficha.motivoRecusa}
          </div>
        )}
        {aprovado && (
          <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-900">
            Sua página está no ar com a versão aprovada{ficha.revisaoPendente ? " (há alterações aguardando revisão)" : ""}. Contatos e logotipo valem na hora;
            as demais alterações só entram na página depois que você enviar para revisão e o eaiRH aprovar.
          </div>
        )}
        {ficha?.status === "aguardando" && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">⏳ Cadastro enviado, aguardando aprovação do eaiRH. Você ainda pode ajustar e enviar de novo.</div>
        )}

        <div className="flex flex-wrap gap-1.5 mt-4">
          {PASSOS.map((t, i) => (
            <button key={t} type="button" disabled={!ficha && i > 0} onClick={() => { setPasso(i); setErro(""); }}
              className={`text-xs rounded-full px-2.5 py-1.5 ${i === passo ? "bg-primary text-white" : i < passo ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"} disabled:opacity-50`}>
              {i + 1}. {t}
            </button>
          ))}
        </div>

        <div className="mt-4 bg-white border border-gray-200 rounded-2xl p-5">
          {salvoEm && <span className="float-right text-xs text-green-700">✓ salvo</span>}
          {telas[passo]}
        </div>

        {erro && <p className="mt-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{erro}</p>}

        <div className="flex justify-between mt-4">
          <button type="button" onClick={() => { setPasso(Math.max(0, passo - 1)); setErro(""); }}
            className={`bg-white border border-gray-300 text-gray-700 font-bold rounded-lg px-4 py-2 text-sm ${passo === 0 ? "invisible" : ""}`}>← Voltar</button>
          {passo < 7 ? (
            <button type="button" disabled={salvando} onClick={() => salvar(passo + 1)} className="bg-accent text-white font-bold rounded-lg px-5 py-2 text-sm disabled:opacity-60">
              {salvando ? "Salvando…" : "Salvar e continuar →"}
            </button>
          ) : (
            <button type="button" disabled={salvando} onClick={enviar} className="bg-accent text-white font-bold rounded-lg px-5 py-2 text-sm disabled:opacity-60">
              {salvando ? "Enviando…" : aprovado ? "Enviar alterações para revisão ✓" : "Enviar para aprovação ✓"}
            </button>
          )}
        </div>
        {ficha && passo < 7 && (
          <p className="text-[11px] text-gray-400 mt-3">Quer parar agora? O que você salvou fica guardado; é só voltar em <Link to="/parceiro" className="underline">Painel do parceiro</Link>.</p>
        )}
      </main>
    </div>
  );
}

// ---------- 1. tipo ----------
function PassoTipo({ dados, muda, travado }) {
  const Op = ({ v, titulo, children }) => (
    <button type="button" disabled={travado} onClick={() => muda("tipo", v)}
      className={`text-left rounded-xl border-2 p-4 ${dados.tipo === v ? "border-primary bg-sky-50" : "border-gray-200 hover:border-primary/50"} disabled:opacity-70`}>
      <b className="text-[15px]">{titulo}</b>
      <span className="block text-[12.5px] text-gray-500 mt-1">{children}</span>
    </button>
  );
  return (
    <>
      <h2 className="text-lg font-bold">Que tipo de parceiro você é?</h2>
      <p className="text-[13px] text-gray-500 mb-3">Isso define o que aparece para o candidato.{travado && " Depois de aprovado, o tipo não muda."}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Op v="escola" titulo="🏫 Escola ou franquia">Oferece BOLSA (% de desconto) no curso. As aulas são na plataforma da escola. O eaiRH entrega o contato de quem tiver interesse, com autorização.</Op>
        <Op v="prof" titulo="👩‍🏫 Professor particular">Oferece AULA EXPERIMENTAL GRÁTIS. Você combina a aula com o candidato: na sala de vídeo do eaiRH, na sua plataforma ou presencial.</Op>
      </div>
    </>
  );
}

// ---------- 2. identificação ----------
function PassoIdentificacao({ dados, muda, ficha, setFicha }) {
  const escola = dados.tipo !== "prof";
  const [doc, setDoc] = useState(null);   // resultado da conferência do documento
  const [logoErro, setLogoErro] = useState("");
  const [enviandoLogo, setEnviandoLogo] = useState(false);
  const r = doc?.receita || (dados.receita && dados.receita.cnpj === soDigitos(dados.documento) ? dados.receita : null);

  const conferir = async () => {
    const d = soDigitos(dados.documento);
    if (d.length !== 11 && d.length !== 14) { setDoc(d ? { valido: false } : null); return; }
    try { setDoc(await conferirDocumentoParceiro(d)); } catch { setDoc(null); }
  };
  const onde = dados.ondeDaAula || [];
  const alternaOnde = (v) => muda("ondeDaAula", onde.includes(v) ? onde.filter((x) => x !== v) : [...onde, v]);

  const escolherLogo = (ev) => {
    const arq = ev.target.files?.[0];
    ev.target.value = "";
    setLogoErro("");
    if (!arq) return;
    if (!/^image\/(png|jpeg)$/.test(arq.type)) { setLogoErro("Use uma imagem PNG ou JPG."); return; }
    if (arq.size > 2 * 1024 * 1024) { setLogoErro("A imagem deve ter até 2 MB."); return; }
    const url = URL.createObjectURL(arq);
    const img = new Image();
    img.onload = async () => {
      URL.revokeObjectURL(url);
      const w = img.naturalWidth, h = img.naturalHeight;
      if (Math.abs(w - h) > Math.max(w, h) * 0.1) { setLogoErro(`A imagem precisa ser quadrada (esta tem ${w}×${h}).`); return; }
      setEnviandoLogo(true);
      try { const res = await enviarLogoParceiro(ficha.id, arq); setFicha((f) => ({ ...f, logoUrl: res.logoUrl })); }
      catch (e) { setLogoErro(e.message); }
      setEnviandoLogo(false);
    };
    img.onerror = () => { URL.revokeObjectURL(url); setLogoErro("Não consegui ler esta imagem."); };
    img.src = url;
  };

  return (
    <>
      <h2 className="text-lg font-bold">Identificação {escola ? "da escola" : ""}</h2>
      <p className="text-[13px] text-gray-500">{escola ? "Conferimos o CNPJ na Receita, como fazemos com toda empresa nova." : "Autônomo não precisa de CNPJ: CPF ou MEI bastam. O eaiRH confere o documento antes de aprovar."}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Rotulo>Nome {escola ? "da escola" : "completo"}</Rotulo>
          <input className={inputCls} maxLength={120} value={dados.nome || ""} onChange={(e) => muda("nome", e.target.value)} />
        </div>
        <div>
          <Rotulo>{escola ? "CNPJ" : "CPF ou CNPJ (MEI)"}</Rotulo>
          <input className={inputCls} inputMode="numeric" maxLength={18} value={dados.documento || ""} onChange={(e) => { muda("documento", e.target.value); setDoc(null); }} onBlur={conferir}
            placeholder={escola ? "00.000.000/0001-00" : "000.000.000-00"} />
          {doc && doc.valido === false && <span className="inline-block mt-1.5 rounded px-2 py-1 text-xs bg-red-50 text-red-700">Documento inválido. Confira os números.</span>}
          {doc?.tipo === "cpf" && doc.valido && <span className="inline-block mt-1.5 rounded px-2 py-1 text-xs bg-green-100 text-green-800">✓ CPF válido</span>}
          {r?.consultado && r.situacao === "ATIVA" && <span className="inline-block mt-1.5 rounded px-2 py-1 text-xs bg-green-100 text-green-800">✓ Ativa na Receita · {r.razaoSocial}</span>}
          {r?.consultado && r.situacao !== "ATIVA" && <span className="inline-block mt-1.5 rounded px-2 py-1 text-xs bg-red-50 text-red-700">Na Receita: {r.situacao}. Só aceitamos CNPJ ativo.</span>}
          {r && !r.consultado && <span className="inline-block mt-1.5 rounded px-2 py-1 text-xs bg-amber-50 text-amber-800">{r.erro || "Não conferido"}. O eaiRH confere antes de aprovar.</span>}
        </div>
      </div>

      {escola && (
        <>
          <Rotulo>É franquia?</Rotulo>
          <div className="flex gap-2">
            <Chip ativo={dados.franquia === true} onClick={() => muda("franquia", true)}>Sim, sou uma unidade</Chip>
            <Chip ativo={dados.franquia === false} onClick={() => muda("franquia", false)}>Não</Chip>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Rotulo>Marca</Rotulo><input className={inputCls} maxLength={80} value={dados.marca || ""} onChange={(e) => muda("marca", e.target.value)} placeholder={dados.nome || "Ex.: Times Idiomas"} /></div>
            {dados.franquia === true && <div><Rotulo>Unidade</Rotulo><input className={inputCls} maxLength={80} value={dados.unidade || ""} onChange={(e) => muda("unidade", e.target.value)} placeholder="Ex.: Trianon-Masp" /></div>}
          </div>
          {dados.franquia === true && (
            <p className="text-[13px] text-gray-500 mt-1.5">A unidade aparece no título da página: <b>{nomeExibicao(dados) || "Marca · Unidade"}</b>. Cada unidade tem a sua página e os seus próprios números (para outra unidade, faça outro cadastro pelo painel).</p>
          )}
        </>
      )}

      {!escola && (
        <>
          <Rotulo>Onde você dá aula</Rotulo>
          <div className="flex flex-wrap gap-2">{ONDE.map(([v, t]) => <Chip key={v} ativo={onde.includes(v)} onClick={() => alternaOnde(v)}>{t}</Chip>)}</div>
          {onde.includes("presencial") && (<><Rotulo>Cidade</Rotulo><input className={inputCls} maxLength={80} value={dados.cidade || ""} onChange={(e) => muda("cidade", e.target.value)} placeholder="Ex.: Rio de Janeiro/RJ" /></>)}
        </>
      )}

      <Rotulo>{escola ? "Logotipo" : "Sua foto ou logotipo"}</Rotulo>
      <div className="flex gap-3.5 items-center">
        <div className="w-20 h-20 shrink-0 rounded-xl border-2 border-dashed border-gray-400 bg-gray-50 flex items-center justify-center overflow-hidden font-extrabold text-primary text-xl">
          {ficha?.logoUrl ? <img src={urlApi(ficha.logoUrl)} alt="Logotipo" className="w-full h-full object-cover" /> : iniciais(nomeExibicao(dados) || dados.nome)}
        </div>
        <div>
          {ficha ? <input type="file" accept="image/png,image/jpeg" onChange={escolherLogo} disabled={enviandoLogo} className="text-sm" /> : <p className="text-xs text-gray-500">Salve esta etapa para enviar a imagem.</p>}
          <p className="text-[12px] text-gray-500 mt-1">{enviandoLogo ? "Enviando…" : "PNG ou JPG, quadrado, até 2 MB. Aparece na vitrine e na sua página."}</p>
          {logoErro && <p className="text-xs text-red-600 mt-1">{logoErro}</p>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div><Rotulo>Site</Rotulo><input className={inputCls} maxLength={200} value={dados.site || ""} onChange={(e) => muda("site", e.target.value)} placeholder="https://" /></div>
        <div><Rotulo>Instagram / LinkedIn</Rotulo><input className={inputCls} maxLength={200} value={dados.social || ""} onChange={(e) => muda("social", e.target.value)} placeholder="@perfil ou link" /></div>
      </div>

      {escola && (
        <>
          <Rotulo>Endereço da unidade (ou marque "100% online")</Rotulo>
          <div className="grid sm:grid-cols-3 gap-3">
            <input className={`${inputCls} sm:col-span-2`} maxLength={200} disabled={dados.online100} value={dados.endereco || ""} onChange={(e) => muda("endereco", e.target.value)} placeholder="Ex.: Av. Paulista, 1000" />
            <input className={inputCls} maxLength={80} disabled={dados.online100} value={dados.cidade || ""} onChange={(e) => muda("cidade", e.target.value)} placeholder="Cidade/UF" />
          </div>
          <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={!!dados.online100} onChange={(e) => muda("online100", e.target.checked)} /> 100% online</label>
        </>
      )}
    </>
  );
}

// ---------- 3. responsáveis ----------
function PassoContatos({ dados, muda }) {
  const lista = dados.contatos && dados.contatos.length ? dados.contatos : [{ ...contatoVazio(), principal: true }];
  const grava = (l) => muda("contatos", l);
  const altera = (i, k, v) => grava(lista.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const principal = (i) => grava(lista.map((c, j) => ({ ...c, principal: j === i })));
  const remover = (i) => {
    const l = lista.filter((_, j) => j !== i);
    if (!l.some((c) => c.principal) && l[0]) l[0] = { ...l[0], principal: true };
    grava(l);
  };
  return (
    <>
      <h2 className="text-lg font-bold">Responsáveis e contatos</h2>
      <p className="text-[13px] text-gray-500">Esses dados NÃO aparecem para o candidato. São para o aviso de interesse e para falarmos com vocês.</p>
      {lista.map((c, i) => (
        <div key={i} className="relative mt-3 rounded-xl border border-gray-200 p-3.5">
          <b className="text-sm">Contato {i + 1}</b>{" "}
          {c.principal && <span className="rounded-full bg-green-100 text-green-800 text-[11px] font-bold px-2 py-0.5">responsável principal</span>}
          {lista.length > 1 && <button type="button" onClick={() => remover(i)} className="absolute right-3 top-2.5 text-xs text-red-700">remover</button>}
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Rotulo>Nome</Rotulo><input className={inputCls} maxLength={120} value={c.nome || ""} onChange={(e) => altera(i, "nome", e.target.value)} /></div>
            <div><Rotulo>Cargo</Rotulo><input className={inputCls} maxLength={80} value={c.cargo || ""} onChange={(e) => altera(i, "cargo", e.target.value)} /></div>
            <div><Rotulo>E-mail</Rotulo><input type="email" className={inputCls} maxLength={200} value={c.email || ""} onChange={(e) => altera(i, "email", e.target.value)} placeholder="nome@escola.com.br" /></div>
            <div><Rotulo>Telefone / WhatsApp</Rotulo><input className={inputCls} inputMode="tel" maxLength={30} value={c.telefone || ""} onChange={(e) => altera(i, "telefone", e.target.value)} placeholder="(11) 91234-5678" /></div>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-2.5 text-[13px] items-center">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!c.recebe} onChange={(e) => altera(i, "recebe", e.target.checked)} /> Recebe os interessados</label>
            <label className="flex items-center gap-1.5"><input type="radio" name="principal" checked={!!c.principal} onChange={() => principal(i)} /> Responsável principal</label>
            <label className="flex items-center gap-1.5">Aviso por:
              <select className="border border-gray-300 rounded px-1.5 py-0.5" value={c.canal || "email"} onChange={(e) => altera(i, "canal", e.target.value)}>
                <option value="email">E-mail</option><option value="whatsapp">WhatsApp</option><option value="ambos">Os dois</option>
              </select>
            </label>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => grava([...lista, contatoVazio()])} disabled={lista.length >= 10}
        className="mt-3 bg-white border border-gray-300 text-gray-700 font-bold rounded-lg px-4 py-2 text-sm disabled:opacity-50">＋ Adicionar outro contato</button>
      <Rotulo>Horário de atendimento</Rotulo>
      <input className={inputCls} maxLength={200} value={dados.horarioAtendimento || ""} onChange={(e) => muda("horarioAtendimento", e.target.value)} placeholder="Ex.: Seg a sex, 8h às 22h · Sáb, 9h às 17h" />
      <Aviso>
        Cada contato marcado em "recebe os interessados" ganha o aviso de cada novo candidato. O <b>responsável principal</b> é quem o eaiRH procura para assuntos da parceria.
        Hoje o aviso sai por <b>e-mail</b>; o aviso por WhatsApp entra assim que os modelos oficiais forem aprovados (a sua preferência já fica guardada).
      </Aviso>
    </>
  );
}

// ---------- 4. sobre ----------
function PassoSobre({ dados, muda, padroes }) {
  const [nova, setNova] = useState("");
  const marcadas = dados.metodologias || [];
  const ehPadrao = (m) => padroes.some((p) => p.toLowerCase() === m.toLowerCase());
  const novas = marcadas.filter((m) => !ehPadrao(m));
  const alterna = (m) => muda("metodologias", marcadas.some((x) => x.toLowerCase() === m.toLowerCase()) ? marcadas.filter((x) => x.toLowerCase() !== m.toLowerCase()) : [...marcadas, m]);
  const adicionar = () => {
    const v = nova.trim().slice(0, 80);
    if (!v) return;
    const igual = padroes.find((p) => p.toLowerCase() === v.toLowerCase());
    if (!marcadas.some((x) => x.toLowerCase() === v.toLowerCase())) muda("metodologias", [...marcadas, igual || v]);
    setNova("");
  };
  const resumo = dados.resumo || "";
  return (
    <>
      <h2 className="text-lg font-bold">Sobre {dados.tipo === "prof" ? "você" : "a escola"}</h2>
      <p className="text-[13px] text-gray-500">É o que o candidato lê na sua página. Sem promessa de emprego ou contratação.</p>
      <Rotulo dica={`(${resumo.length}/500)`}>Resumo</Rotulo>
      <textarea className={inputCls} rows={4} maxLength={500} value={resumo} onChange={(e) => muda("resumo", e.target.value)}
        placeholder="Ex.: Curso de inglês com foco em conversação e metodologia baseada em intercâmbio." />
      <Rotulo dica="(marque todas que se aplicam)">Metodologia</Rotulo>
      <div className="flex flex-wrap gap-1.5">
        {padroes.map((m) => <Chip key={m} ativo={marcadas.some((x) => x.toLowerCase() === m.toLowerCase())} onClick={() => alterna(m)}>{m}</Chip>)}
        {novas.map((m) => <Chip key={m} ativo onClick={() => alterna(m)}>{m} <i className="text-[11px] opacity-75">(nova)</i></Chip>)}
      </div>
      <div className="flex gap-2 mt-2">
        <input className={inputCls} maxLength={80} value={nova} onChange={(e) => setNova(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); adicionar(); } }}
          placeholder="Não achou? Escreva a sua metodologia (ex.: Método Callan)" />
        <button type="button" onClick={adicionar} className="shrink-0 bg-white border border-gray-300 text-gray-700 font-bold rounded-lg px-3 text-sm">＋ Adicionar</button>
      </div>
      <p className="text-[12px] text-gray-500 mt-1.5">As novas entram marcadas como "(nova)". O eaiRH revisa e, se várias escolas usarem a mesma, ela vira opção padrão para todos.</p>
      <Rotulo dica="(opcional)">Diferenciais</Rotulo>
      <input className={inputCls} maxLength={300} value={dados.diferenciais || ""} onChange={(e) => muda("diferenciais", e.target.value)} placeholder="Ex.: professor nativo, turma reduzida, aula ao vivo" />
      <Rotulo dica="(opcional)">Certificações</Rotulo>
      <input className={inputCls} maxLength={200} value={dados.certificacoes || ""} onChange={(e) => muda("certificacoes", e.target.value)} placeholder="Ex.: Cambridge, TOEFL" />
    </>
  );
}

// ---------- 5. cursos ----------
function PassoCursos({ dados, muda }) {
  const lst = (k) => dados[k] || [];
  const alterna = (k, v) => muda(k, lst(k).includes(v) ? lst(k).filter((x) => x !== v) : [...lst(k), v]);
  const semPrazo = dados.duracaoUnidade === "sem-prazo";
  return (
    <>
      <h2 className="text-lg font-bold">O que você ensina</h2>
      <p className="text-[13px] text-gray-500">Ligamos o seu curso às habilidades que os candidatos querem desenvolver. Idiomas aparecem em destaque.</p>
      <Rotulo>Área</Rotulo>
      <div className="flex flex-wrap gap-1.5">{AREAS.map(([v, t]) => <Chip key={v} ativo={dados.area === v} onClick={() => muda("area", v)}>{t}</Chip>)}</div>
      {dados.area === "outra" && (<><Rotulo>Qual área?</Rotulo><input className={inputCls} maxLength={60} value={dados.areaOutra || ""} onChange={(e) => muda("areaOutra", e.target.value)} placeholder="Ex.: Oratória" /></>)}
      {dados.area === "idiomas" && (
        <div className="mt-2 rounded-xl border-2 border-primary bg-sky-50 p-3">
          <b>🌎 Idiomas</b> <span className="rounded-full bg-green-100 text-green-800 text-[11px] font-bold px-2 py-0.5">destaque na vitrine</span>
          <Rotulo>Idiomas</Rotulo>
          <div className="flex flex-wrap gap-1.5">{IDIOMAS.map((v) => <Chip key={v} ativo={lst("idiomas").includes(v)} onClick={() => alterna("idiomas", v)}>{v}</Chip>)}</div>
          <Rotulo>Níveis</Rotulo>
          <div className="flex flex-wrap gap-1.5">{NIVEIS.map((v) => <Chip key={v} ativo={lst("niveis").includes(v)} onClick={() => alterna("niveis", v)}>{v}</Chip>)}</div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Rotulo>Modalidade</Rotulo>
          <select className={inputCls} value={dados.modalidade || ""} onChange={(e) => muda("modalidade", e.target.value)}>
            <option value="">Escolha…</option>
            {["Online ao vivo", "Presencial", "Híbrido", "Gravado"].map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <Rotulo>Duração do curso</Rotulo>
          <div className="flex gap-2">
            <input type="number" min={1} max={999} className={`${inputCls} !w-24`} disabled={semPrazo} value={semPrazo ? "" : dados.duracaoNum ?? ""}
              onChange={(e) => muda("duracaoNum", num(e.target.value))} />
            <select className={inputCls} value={dados.duracaoUnidade || ""} onChange={(e) => { muda("duracaoUnidade", e.target.value); if (e.target.value === "sem-prazo") muda("duracaoNum", null); }}>
              <option value="">unidade…</option>
              {UNIDADES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </div>
        </div>
      </div>

      <Rotulo>Frequência</Rotulo>
      <div className="rounded-xl border border-gray-200 p-3 space-y-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-16">Mínimo</span>
          <input type="number" min={1} max={99} className={`${inputCls} !w-20`} value={dados.freqMin ?? ""} onChange={(e) => muda("freqMin", num(e.target.value))} />
          <span>aulas por</span>
          <select className={`${inputCls} !w-auto`} value={dados.freqMinPor || ""} onChange={(e) => muda("freqMinPor", e.target.value)}>
            <option value="">…</option><option value="dia">dia</option><option value="semana">semana</option><option value="mes">mês</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-16">Máximo</span>
          <input type="number" min={1} max={99} className={`${inputCls} !w-20`} disabled={!!dados.freqSemLimite} value={dados.freqSemLimite ? "" : dados.freqMax ?? ""} onChange={(e) => muda("freqMax", num(e.target.value))} />
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!dados.freqSemLimite} onChange={(e) => { muda("freqSemLimite", e.target.checked); if (e.target.checked) muda("freqMax", null); }} /> sem limite</label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span>Duração de cada aula</span>
          <input type="number" min={1} max={600} className={`${inputCls} !w-20`} value={dados.aulaDuracaoNum ?? ""} onChange={(e) => muda("aulaDuracaoNum", num(e.target.value))} />
          <select className={`${inputCls} !w-auto`} value={dados.aulaDuracaoUnidade || ""} onChange={(e) => muda("aulaDuracaoUnidade", e.target.value)}>
            <option value="">…</option><option value="min">min</option><option value="h">h</option>
          </select>
        </div>
        <input className={inputCls} maxLength={120} value={dados.freqObs || ""} onChange={(e) => muda("freqObs", e.target.value)} placeholder="Observação curta (opcional). Ex.: horários livres de seg a sáb" />
      </div>

      <Rotulo>Formato</Rotulo>
      <select className={inputCls} value={dados.formato || ""} onChange={(e) => muda("formato", e.target.value)}>
        <option value="">Escolha…</option>
        {["Turma e individual", "Só individual", "Só turma"].map((m) => <option key={m}>{m}</option>)}
      </select>
    </>
  );
}

// ---------- 6. condições ----------
function PassoCondicoes({ dados, muda }) {
  if (dados.tipo === "prof") {
    return (
      <>
        <h2 className="text-lg font-bold">Sua aula experimental grátis</h2>
        <p className="text-[13px] text-gray-500">Sem cartão e sem contrato. Depois dela o candidato decide se continua.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Rotulo>Duração da aula grátis (min)</Rotulo><input type="number" min={10} max={600} className={inputCls} value={dados.aulaGratisMin ?? ""} onChange={(e) => muda("aulaGratisMin", num(e.target.value))} placeholder="30" /></div>
          <div><Rotulo dica="(opcional)">Desconto para quem vem do eaiRH (%)</Rotulo><input type="number" min={0} max={100} className={inputCls} value={dados.descontoPct ?? ""} onChange={(e) => muda("descontoPct", num(e.target.value))} placeholder="10" /></div>
        </div>
        <Rotulo dica="(opcional)">Valor do curso depois</Rotulo>
        <input className={inputCls} maxLength={120} value={dados.valorCurso || ""} onChange={(e) => muda("valorCurso", e.target.value)} placeholder="Ex.: A partir de R$ 100/mês (4 aulas)" />
      </>
    );
  }
  return (
    <>
      <h2 className="text-lg font-bold">A condição para quem vem do eaiRH</h2>
      <p className="text-[13px] text-gray-500">Tudo aqui aparece para o candidato ANTES de ele pedir o contato. Transparência é obrigatória.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Rotulo>Bolsa eaiRH (%)</Rotulo><input type="number" min={1} max={100} className={inputCls} value={dados.bolsaPct ?? ""} onChange={(e) => muda("bolsaPct", num(e.target.value))} /></div>
        <div><Rotulo>Validade da bolsa</Rotulo><input className={inputCls} maxLength={120} value={dados.bolsaValidade || ""} onChange={(e) => muda("bolsaValidade", e.target.value)} placeholder="Ex.: Durante todo o curso" /></div>
      </div>
      <Rotulo>Quem pode ter a bolsa</Rotulo>
      <textarea className={inputCls} rows={2} maxLength={400} value={dados.elegibilidade || ""} onChange={(e) => muda("elegibilidade", e.target.value)} placeholder="Ex.: A escola avalia a elegibilidade e as condições comerciais numa conversa com você." />
      <Rotulo>Fidelidade / cancelamento</Rotulo>
      <textarea className={inputCls} rows={2} maxLength={400} value={dados.cancelamento || ""} onChange={(e) => muda("cancelamento", e.target.value)} placeholder="Ex.: Sem fidelidade; cancela quando quiser." />
      <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={!!dados.temTaxa} onChange={(e) => muda("temTaxa", e.target.checked)} /> Há taxa ou multa de cancelamento</label>
      {dados.temTaxa && (<><Rotulo>Valor ou regra da taxa</Rotulo><input className={inputCls} maxLength={200} value={dados.taxaRegra || ""} onChange={(e) => muda("taxaRegra", e.target.value)} placeholder="Ex.: 10% das parcelas restantes" /></>)}
      <Aviso>Se houver taxa ou multa, informe o valor ou a regra. Candidato que se sentir surpreendido pode avaliar a escola publicamente.</Aviso>
      <Rotulo dica="(opcional)">Preço "a partir de"</Rotulo>
      <input className={inputCls} maxLength={80} value={dados.precoAPartir || ""} onChange={(e) => muda("precoAPartir", e.target.value)} placeholder="R$ ..." />
      <Rotulo>Teste de nível grátis?</Rotulo>
      <div className="flex gap-2">
        <Chip ativo={dados.testeNivelGratis === true} onClick={() => muda("testeNivelGratis", true)}>Sim</Chip>
        <Chip ativo={dados.testeNivelGratis === false} onClick={() => muda("testeNivelGratis", false)}>Não</Chip>
      </div>
    </>
  );
}

// ---------- 7. compromissos ----------
function PassoCompromissos({ dados, muda }) {
  const c = COMPROMISSOS.map((_, i) => !!(dados.compromissos || [])[i]);
  return (
    <>
      <h2 className="text-lg font-bold">Compromissos com o candidato</h2>
      <p className="text-[13px] text-gray-500">Valem para todos os parceiros. Sem eles, o cadastro não é enviado.</p>
      {COMPROMISSOS.map((t, i) => (
        <label key={i} className="flex gap-2 items-start text-sm my-2.5 cursor-pointer">
          <input type="checkbox" className="mt-1" checked={c[i]} onChange={(e) => muda("compromissos", c.map((x, j) => (j === i ? e.target.checked : x)))} />
          <span>{t}</span>
        </label>
      ))}
      <Aviso>Na sua página, o eaiRH mostra números que você não edita: tempo médio de resposta, % de interessados contatados e nota dos alunos.</Aviso>
    </>
  );
}

// ---------- 8. revisar ----------
function PassoRevisar({ dados, ficha, pendencias, irPara }) {
  const escola = dados.tipo !== "prof";
  const nome = nomeExibicao(dados) || "(sem nome)";
  const curso = cursoDe(dados);
  const itens = escola
    ? [
      dados.bolsaPct && `Bolsa: ${dados.bolsaPct}%${dados.bolsaValidade ? `, ${dados.bolsaValidade.charAt(0).toLowerCase()}${dados.bolsaValidade.slice(1)}` : ""}`,
      [duracaoDe(dados), frequenciaDe(dados)].filter(Boolean).join(" · "),
      dados.cancelamento && `Cancelamento: ${dados.cancelamento}${dados.temTaxa && dados.taxaRegra ? ` Taxa: ${dados.taxaRegra}` : ""}`,
      dados.elegibilidade && `Quem pode ter a bolsa: ${dados.elegibilidade}`,
      dados.testeNivelGratis === true && "Teste de nível grátis",
    ]
    : [
      dados.aulaGratisMin && `Aula experimental de ${dados.aulaGratisMin} min grátis`,
      dados.descontoPct ? `${dados.descontoPct}% de desconto para quem vem do eaiRH` : null,
      [duracaoDe(dados), frequenciaDe(dados)].filter(Boolean).join(" · "),
      dados.valorCurso && `Valor do curso: ${dados.valorCurso}`,
    ];
  return (
    <>
      <h2 className="text-lg font-bold">Confira como o candidato vai ver</h2>
      <p className="text-[13px] text-gray-500">Depois de enviar, o eaiRH confere e aprova (normalmente no mesmo dia). Você recebe um e-mail e já acessa o painel.</p>
      <div className="mt-3 rounded-xl border border-dashed border-gray-400 bg-gray-50 p-4">
        <div className="flex gap-3 items-start">
          <div className="w-12 h-12 shrink-0 rounded-lg bg-accent text-white font-bold flex items-center justify-center overflow-hidden">
            {ficha?.logoUrl ? <img src={urlApi(ficha.logoUrl)} alt="" className="w-full h-full object-cover" /> : iniciais(nome)}
          </div>
          <div className="min-w-0">
            {/* 31/10/2026: sem a bolsa preenchida a prévia mostrava "?% de bolsa". Sem número, o número some; a
                falta já aparece na lista "Falta ajustar antes de enviar" logo abaixo. */}
            <b className="text-lg">{escola ? `${nome}: ${curso}${dados.bolsaPct > 0 ? ` com ${dados.bolsaPct}% de bolsa` : " com bolsa"}` : `${nome}: aula grátis de ${curso}`}</b>
            {dados.resumo && <p className="text-sm text-gray-600 mt-0.5">{dados.resumo}</p>}
          </div>
        </div>
        <ul className="list-disc pl-5 mt-2 text-sm text-gray-800">{itens.filter(Boolean).map((t) => <li key={t}>{t}</li>)}</ul>
        {(dados.metodologias || []).length > 0 && <div className="flex flex-wrap gap-1 mt-2">{dados.metodologias.map((m) => <span key={m} className="rounded-full bg-sky-100 text-sky-900 text-xs px-2 py-0.5">{m}</span>)}</div>}
        <span className="inline-block mt-3 bg-accent text-white font-bold rounded-lg px-3.5 py-1.5 text-[13px]">{escola ? "Tenho interesse" : "Pedir aula grátis"}</span>
      </div>
      {ficha && <p className="text-sm mt-2"><Link to={`/capacitacao/${ficha.slug}?previa=1`} target="_blank" className="text-primary font-semibold underline">Ver a página completa (prévia)</Link></p>}
      {pendencias.length > 0 && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm">
          <b className="text-red-800">Falta ajustar antes de enviar:</b>
          <ul className="mt-1 space-y-1">
            {pendencias.map((p, i) => (
              <li key={i}><button type="button" onClick={() => irPara(p.passo)} className="text-left text-red-800 underline">{p.passo + 1}. {PASSOS[p.passo]}: {p.msg}</button></li>
            ))}
          </ul>
        </div>
      )}
      <Aviso>{ficha?.status === "aprovado" ? "Ao enviar: as alterações vão para revisão. A página continua com a versão aprovada até o eaiRH conferir." : "Ao enviar: status \"Aguardando aprovação\". Você recebe e-mail quando estiver no ar."}</Aviso>
    </>
  );
}
